import React, { useEffect, useState } from 'react';
import { WButton, WMenu, WTypography } from '../../wrappers/wrapper';
import styles from './member-filter.module.scss';
import { Checkbox, Divider, FormControl, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import AddOutlinedIcon from '@mui/icons-material/AddOutlined';
import { IQueryParams, TTitle } from '../../../models/common';
import dbService from '../../../services/database.service';
import Button from 'components/atoms/button/button';
import Input from 'components/molecules/input/input';

interface Filter {
  username?: { $regex: string };
  title?: { $in: string[] };
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const slotProps = {
  paper: {
    elevation: 0,
    sx: {
      border: 'var(--border-light)',
      padding: '1rem',
      width: '22rem',
      borderRadius: 'var(--border-radius)',
      backgroundColor: 'var(--color-content-background)',
      color: "var(--color-white)",
    },
  },
};

interface MemberFilterProps {
  onFilterChanged: (value: IQueryParams) => void;
}

function MemberFilter({ onFilterChanged }: MemberFilterProps) {
  const [titleData, setATitleData] = useState<TTitle[]>([]);

  useEffect(() => {
    dbService.getTitles({ relation: true }).then((res) => setATitleData(res));
  }, []);

  const [anchorEl, setAnchorEl] = React.useState<HTMLButtonElement | null>(null);
  const [anchorElTags, setAnchorElTags] = React.useState<HTMLButtonElement | null>(null);
  const [enteredName, setEnteredName] = React.useState('');
  const [tags, setTags] = React.useState<string[]>([]);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClickTags = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorElTags(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleCloseTags = () => {
    setAnchorElTags(null);
  };

  const handleChange = (event: SelectChangeEvent<typeof tags>) => {
    const {
      target: { value },
    } = event;
    setTags(typeof value === 'string' ? value.split(',') : value);
  };

  const getTagsIds = (): string[] => {
    return tags.map((tag) => titleData.find((title) => title.title === tag)?._id!).filter(Boolean);
  };

  const handleApplyFilter = async () => {
    const filter: Filter = { username: { $regex: enteredName } };
    const tagsIds = getTagsIds();
    if (tagsIds.length) {
      filter.title = { $in: tagsIds };
    }
    handleClose();
    handleCloseTags();
    onFilterChanged(filter);
  };

  const open = Boolean(anchorEl);
  const open2 = Boolean(anchorElTags);

  return (
    <div className={styles['filter']}>
      <div className={styles['filter-buttons']}>
        <WButton className={`${styles['button']} ${styles['chip']}`} onClick={handleClick}>
          <AddOutlinedIcon className={styles['outlined-icon']}></AddOutlinedIcon>
          Name
          {enteredName && (
            <div className={styles['name-display']}>
              <div className={styles['name-display-divide']} />
              {enteredName}
            </div>
          )}
        </WButton>

        <WMenu
          anchorEl={anchorEl}
          open={open}
          onClose={handleClose}
          slotProps={slotProps}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          className={styles.menu}
          
        >
          <div className={styles.popoverBox}>
            <WTypography className={styles.title}>Search Name</WTypography>
            <Divider style={{border:"var(--border-light)"}} />
            <Input
              placeholder="Full Name"
              value={enteredName}
              className={styles.input}
              onChange={(e) => setEnteredName(e.target.value)}
            />
            <Button className={styles.applyButton} onClick={handleApplyFilter}>
              Apply
            </Button>
          </div>
        </WMenu>

        <WButton className={`${styles['button']} ${styles['chip']}`} onClick={handleClickTags}>
          <AddOutlinedIcon className={styles['outlined-icon']}></AddOutlinedIcon>
          Tag
        </WButton>

        <WMenu
          anchorEl={anchorElTags}
          open={open2}
          onClose={handleCloseTags}
          slotProps={slotProps}
          transformOrigin={{ horizontal: 'left', vertical: 'top' }}
          anchorOrigin={{ horizontal: 'left', vertical: 'bottom' }}
          className={styles.menu}
        >
          <div className={styles.popoverBox}>
            <WTypography className={styles.title}>Tags</WTypography>
            <Divider style={{border:"var(--border-light)"}}  />
            <FormControl>
              <Select
                placeholder="Tag"
                variant="standard"
                multiple
                value={tags}
                onChange={handleChange}
                renderValue={(selected) => selected.join(', ')}
                MenuProps={MenuProps}
                sx={{
                  height: '46px',
                  border: 'var(--border-light)',
                  outline: 'none',
                  padding: '0.75rem 1rem',
                  borderRadius: '0.5rem',
                  background: 'var(--color-content-background-dark)',
                  color: 'var(--color-white)',
                  fontSize: '0.875rem',
                  '.MuiInputBase-input': {
                    background: 'none !important',
                  },
                  '.MuiOutlinedInput-notchedOutline': {
                    height: '46px',
                  },
                  '&.Mui-focused': {
                    backgroundColor: 'transparent',
                  },
                  '.MuiSelect-select': {
                    display: 'flex',
                    alignItems: 'center',
                    paddingTop: 0,
                    paddingBottom: 0,
                    height: '46px',
                    
                  },
                  '&:before, &:after': {
                    display: 'none',
                  },
                  '&:hover:not(.Mui-disabled):before': {
                    display: 'none',
                  },
                }}
              >
                {titleData.map((data) => (
                  <MenuItem style={{background:"var(--color-content-background-dark)", color:"var(--color-white)"}} key={data._id} value={data.title}>
                    <Checkbox style={{color:"var(--color-white)"}} checked={tags.includes(data.title!)} />
                    <span className={styles.listItem}>{data.title}</span>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button className={styles.applyButton} onClick={handleApplyFilter}>
              Apply
            </Button>
          </div>
        </WMenu>
      </div>
    </div>
  );
}
export default MemberFilter;
